import type {
    HistoryChange,
    AddHistoryChange,
    AddHistoryChangePayload,
    CopyHistoryChange,
    CopyHistoryChangePayload,
    RemoveHistoryChange,
    RemoveHistoryChangePayload,
    MoveHistoryChange,
    MoveHistoryChangePayload,
    UpdateHistoryChange,
    UpdateHistoryChangePayload,
    UpdateThemeHistoryChange,
    UpdateThemeHistoryChangePayload,
} from './history.model';
import { Injectable, inject } from '@angular/core';
import type { Update } from '@ngrx/entity';

import { ThemeStateService } from '@sites/site-theme/src/state';
import { BlocksFacade } from '@web/store/page-structure/blocks/blocks.facade';
import { SectionsFacade } from '@web/store/page-structure/sections/sections.facade';
import { ColumnsFacade } from '@web/store/page-structure/columns/columns.facade';
import { GridsFacade } from '@web/store/page-structure/grids/grids.facade';
import { WidgetsFacade } from '@web/store/page-structure/widgets/widgets.facade';
import { TabsFacade } from '@web/store/page-structure/tabs/tabs.facade';
import { ShopCartIconFacade } from '@web/store/shop-cart-icon/shop-cart-icon.facade';

import { HistoryChangeAction } from './history.model';

@Injectable({
    providedIn: 'root',
})
export class HistoryService {
    private readonly themeState = inject(ThemeStateService);
    private readonly blocksFacade = inject(BlocksFacade);
    private readonly sectionsFacade = inject(SectionsFacade);
    private readonly columnsFacade = inject(ColumnsFacade);
    private readonly gridsFacade = inject(GridsFacade);
    private readonly widgetsFacade = inject(WidgetsFacade);
    private readonly tabsFacade = inject(TabsFacade);
    private readonly shopCartIconFacade = inject(ShopCartIconFacade);

    public createAddHistoryChange(payload: AddHistoryChangePayload): AddHistoryChange {
        return {
            action: HistoryChangeAction.add,
            payload,
        };
    }

    public createCopyHistoryChange(payload: CopyHistoryChangePayload): CopyHistoryChange {
        return {
            action: HistoryChangeAction.copy,
            payload,
        };
    }

    public createRemoveHistoryChange(payload: RemoveHistoryChangePayload): RemoveHistoryChange {
        return {
            action: HistoryChangeAction.remove,
            payload,
        };
    }

    public createMoveHistoryChange(payload: MoveHistoryChangePayload): MoveHistoryChange {
        return {
            action: HistoryChangeAction.move,
            payload,
        };
    }

    public createUpdateHistoryChange(payload: UpdateHistoryChangePayload): UpdateHistoryChange {
        return {
            action: HistoryChangeAction.update,
            payload,
        };
    }

    public createThemeUpdateHistoryChange(payload: UpdateThemeHistoryChangePayload): UpdateThemeHistoryChange {
        return {
            action: HistoryChangeAction.update,
            payload,
        };
    }

    public handleUndo(historyChange: HistoryChange): void {
        switch (historyChange.action) {
            case HistoryChangeAction.add:
                return this.handleAddUndo(historyChange);
            case HistoryChangeAction.copy:
                return this.handleCopyUndo(historyChange);
            case HistoryChangeAction.remove:
                return this.handleRemoveUndo(historyChange);
            case HistoryChangeAction.move:
                return this.handleMoveUndo(historyChange);
            case HistoryChangeAction.update:
                return this.handleUpdateUndo(historyChange);
        }
    }

    private handleAddUndo(historyChange: AddHistoryChange): void {
        if (historyChange.payload.entityType === 'block') {
            return this.blocksFacade.removeBlock(historyChange.payload.entity, historyChange.payload.index, true);
        }

        if (historyChange.payload.entityType === 'section') {
            return this.sectionsFacade.removeSection(historyChange.payload.entity, historyChange.payload.index, true);
        }

        if (historyChange.payload.entityType === 'column') {
            return this.columnsFacade.removeColumn(historyChange.payload.entity, historyChange.payload.index, true);
        }

        if (historyChange.payload.entityType === 'grid') {
            return this.gridsFacade.remove(historyChange.payload.entity, historyChange.payload.index, true);
        }

        if (historyChange.payload.entityType === 'legacyGridWidget') {
            return this.widgetsFacade.removeWidgetFromGrid(
                historyChange.payload.entity,
                historyChange.payload.index,
                historyChange.payload.gridCellIndex,
                true
            );
        }

        if (historyChange.payload.entityType === 'legacyWidget') {
            return this.widgetsFacade.removeWidget(historyChange.payload.entity, historyChange.payload.index, true);
        }

        if (historyChange.payload.entityType === 'tab') {
            return this.tabsFacade.remove(historyChange.payload.entity, historyChange.payload.index, true);
        }
    }

    private handleCopyUndo(historyChange: CopyHistoryChange): void {
        if (historyChange.payload.entityType === 'block') {
            return this.blocksFacade.removeBlock(historyChange.payload.entity, historyChange.payload.index, true);
        }

        if (historyChange.payload.entityType === 'column') {
            return this.columnsFacade.removeColumn(historyChange.payload.entity, historyChange.payload.index, true);
        }

        if (historyChange.payload.entityType === 'grid') {
            return this.gridsFacade.remove(historyChange.payload.entity, historyChange.payload.index, true);
        }

        if (historyChange.payload.entityType === 'legacyGridWidget') {
            return this.widgetsFacade.removeWidgetFromGrid(
                historyChange.payload.entity,
                historyChange.payload.index,
                historyChange.payload.gridCellIndex,
                true
            );
        }

        if (historyChange.payload.entityType === 'legacyWidget') {
            return this.widgetsFacade.removeWidget(historyChange.payload.entity, historyChange.payload.index, true);
        }

        if (historyChange.payload.entityType === 'tab') {
            return this.tabsFacade.remove(historyChange.payload.entity, historyChange.payload.index, true);
        }

        console.warn('Unknown entity type', historyChange.payload.entityType);
    }

    private handleRemoveUndo(historyChange: RemoveHistoryChange): void {
        if (historyChange.payload.entityType === 'block') {
            return this.blocksFacade.addBlock(historyChange.payload.entity, historyChange.payload.index);
        }

        if (historyChange.payload.entityType === 'section') {
            this.sectionsFacade.addSection(
                {
                    ...historyChange.payload.entity,
                    children: [],
                },
                historyChange.payload.index
            );

            historyChange.payload.entity.children.forEach((column, index) => {
                this.columnsFacade.addColumn(column, index);
            });
            return;
        }

        if (historyChange.payload.entityType === 'column') {
            return this.columnsFacade.addColumn(historyChange.payload.entity, historyChange.payload.index);
        }

        if (historyChange.payload.entityType === 'grid') {
            return this.gridsFacade.add(historyChange.payload.entity, historyChange.payload.index);
        }

        if (historyChange.payload.entityType === 'legacyGridWidget') {
            return this.widgetsFacade.addWidgetToGrid(
                historyChange.payload.entity,
                historyChange.payload.index,
                historyChange.payload.gridCellIndex
            );
        }

        if (historyChange.payload.entityType === 'legacyWidget') {
            return this.widgetsFacade.addWidget(historyChange.payload.entity, historyChange.payload.index);
        }

        if (historyChange.payload.entityType === 'tab') {
            this.tabsFacade.add(historyChange.payload.entity, historyChange.payload.index);
            historyChange.payload.entity.children.forEach((child, index) => {
                this.gridsFacade.add(child, index);
            });
            return;
        }

        console.warn('Unknown entity type', historyChange.payload.entityType);
    }

    private handleMoveUndo(historyChange: MoveHistoryChange): void {
        if (historyChange.payload.entityType === 'block') {
            if (historyChange.payload.positionTo < historyChange.payload.positionFrom) {
                return this.blocksFacade.moveDown(historyChange.payload.positionTo, true);
            }
            return this.blocksFacade.moveUp(historyChange.payload.positionTo, true);
        }

        if (historyChange.payload.entityType === 'column') {
            return this.columnsFacade.moveColumnToSection(
                historyChange.payload.entityId,
                historyChange.payload.nextParentId,
                historyChange.payload.prevParentId,
                historyChange.payload.positionTo,
                historyChange.payload.positionFrom,
                true
            );
        }

        if (historyChange.payload.entityType === 'legacyGridWidget') {
            if (historyChange.payload.type === 'fromGridToColumn') {
                return this.gridsFacade.moveWidgetFromColumnToGrid(
                    historyChange.payload.entityId,
                    historyChange.payload.fromCell,
                    historyChange.payload.nextParentId,
                    historyChange.payload.prevParentId,
                    historyChange.payload.positionTo,
                    historyChange.payload.positionFrom,
                    true
                );
            }

            if (historyChange.payload.type === 'fromColumnToGrid') {
                return this.gridsFacade.moveWidgetFromGridToColumn(
                    historyChange.payload.entityId,
                    historyChange.payload.toCell,
                    historyChange.payload.nextParentId,
                    historyChange.payload.prevParentId,
                    historyChange.payload.positionTo,
                    historyChange.payload.positionFrom,
                    true
                );
            }

            if (historyChange.payload.type === 'fromGridToGrid') {
                return this.gridsFacade.moveWidgetFromGridToGrid(
                    historyChange.payload.entityId,
                    historyChange.payload.toCell,
                    historyChange.payload.fromCell,
                    historyChange.payload.nextParentId,
                    historyChange.payload.prevParentId,
                    historyChange.payload.positionTo,
                    historyChange.payload.positionFrom,
                    true
                );
            }
        }

        if (historyChange.payload.entityType === 'legacyWidget') {
            return this.widgetsFacade.moveWidgetToColumn(
                historyChange.payload.entityId,
                historyChange.payload.nextParentId,
                historyChange.payload.prevParentId,
                historyChange.payload.positionTo,
                historyChange.payload.positionFrom,
                true
            );
        }

        console.warn('Unknown entity type', historyChange.payload.entityType);
    }

    private handleUpdateUndo(historyChange: UpdateHistoryChange | UpdateThemeHistoryChange): void {
        if (historyChange.payload.entityType === 'block') {
            return this.blocksFacade.updateBlockProperties(
                historyChange.payload.before.id as string,
                historyChange.payload.before.changes
            );
        }

        if (historyChange.payload.entityType === 'columns') {
            return this.columnsFacade.updateColumnsProperties(historyChange.payload.before);
        }

        if (historyChange.payload.entityType === 'grids') {
            return this.gridsFacade.updateManyProperties(historyChange.payload.before);
        }

        if (historyChange.payload.entityType === 'grid') {
            return this.gridsFacade.updateProperties(
                historyChange.payload.before.id as string,
                historyChange.payload.before.changes
            );
        }

        if (historyChange.payload.entityType === 'legacyWidget') {
            return this.widgetsFacade.updateLegacyWidgetProperties(
                historyChange.payload.before.id as string,
                historyChange.payload.before.changes
            );
        }

        if (historyChange.payload.entityType === 'tab') {
            return this.tabsFacade.updateProperties(
                historyChange.payload.before.id as string,
                historyChange.payload.before.changes
            );
        }

        if (historyChange.payload.entityType === 'store') {
            this.shopCartIconFacade.saveCartIcon(historyChange.payload.before.changes as any);
        }

        if (historyChange.payload.entityType === 'theme') {
            return this.themeState.restore(historyChange.payload.before);
        }

        console.warn('Unknown entity type', historyChange.payload.entityType);
    }

    public handleRedo(historyChange: HistoryChange): void {
        switch (historyChange.action) {
            case HistoryChangeAction.add:
                return this.handleAddRedo(historyChange);
            case HistoryChangeAction.copy:
                return this.handleCopyRedo(historyChange);
            case HistoryChangeAction.remove:
                return this.handleRemoveRedo(historyChange);
            case HistoryChangeAction.move:
                return this.handleMoveRedo(historyChange);
            case HistoryChangeAction.update:
                return this.handleUpdateRedo(historyChange);
        }
    }

    private handleAddRedo(historyChange: AddHistoryChange): void {
        if (historyChange.payload.entityType === 'block') {
            return this.blocksFacade.addBlock(historyChange.payload.entity, historyChange.payload.index);
        }

        if (historyChange.payload.entityType === 'section') {
            return this.sectionsFacade.addSection(historyChange.payload.entity, historyChange.payload.index);
        }

        if (historyChange.payload.entityType === 'column') {
            return this.columnsFacade.addColumn(historyChange.payload.entity, historyChange.payload.index);
        }

        if (historyChange.payload.entityType === 'grid') {
            return this.gridsFacade.add(historyChange.payload.entity, historyChange.payload.index);
        }

        if (historyChange.payload.entityType === 'legacyGridWidget') {
            return this.widgetsFacade.addWidgetToGrid(
                historyChange.payload.entity,
                historyChange.payload.index,
                historyChange.payload.gridCellIndex
            );
        }

        if (historyChange.payload.entityType === 'legacyWidget') {
            return this.widgetsFacade.addWidget(historyChange.payload.entity, historyChange.payload.index);
        }

        if (historyChange.payload.entityType === 'tab') {
            this.tabsFacade.add(historyChange.payload.entity, historyChange.payload.index);
            historyChange.payload.entity.children.forEach((child, index) => {
                this.gridsFacade.add(child, index);
            });
        }
    }

    private handleCopyRedo(historyChange: CopyHistoryChange): void {
        if (historyChange.payload.entityType === 'block') {
            return this.blocksFacade.addBlock(historyChange.payload.entity, historyChange.payload.index);
        }

        if (historyChange.payload.entityType === 'column') {
            return this.columnsFacade.addColumn(historyChange.payload.entity, historyChange.payload.index);
        }

        if (historyChange.payload.entityType === 'grid') {
            return this.gridsFacade.add(historyChange.payload.entity, historyChange.payload.index);
        }

        if (historyChange.payload.entityType === 'legacyGridWidget') {
            return this.widgetsFacade.addWidgetToGrid(
                historyChange.payload.entity,
                historyChange.payload.index,
                historyChange.payload.gridCellIndex
            );
        }

        if (historyChange.payload.entityType === 'legacyWidget') {
            return this.widgetsFacade.addWidget(historyChange.payload.entity, historyChange.payload.index);
        }

        if (historyChange.payload.entityType === 'tab') {
            this.tabsFacade.add(historyChange.payload.entity, historyChange.payload.index);
            historyChange.payload.entity.children.forEach((child, index) => {
                this.gridsFacade.add(child, index);
            });
        }
    }

    private handleRemoveRedo(historyChange: RemoveHistoryChange): void {
        if (historyChange.payload.entityType === 'block') {
            return this.blocksFacade.removeBlock(historyChange.payload.entity, historyChange.payload.index, true);
        }

        if (historyChange.payload.entityType === 'section') {
            this.sectionsFacade.removeSection(historyChange.payload.entity, historyChange.payload.index, true);
            return;
        }

        if (historyChange.payload.entityType === 'column') {
            return this.columnsFacade.removeColumn(historyChange.payload.entity, historyChange.payload.index, true);
        }

        if (historyChange.payload.entityType === 'grid') {
            return this.gridsFacade.remove(historyChange.payload.entity, historyChange.payload.index, true);
        }

        if (historyChange.payload.entityType === 'legacyGridWidget') {
            return this.widgetsFacade.removeWidgetFromGrid(
                historyChange.payload.entity,
                historyChange.payload.index,
                historyChange.payload.gridCellIndex,
                true
            );
        }

        if (historyChange.payload.entityType === 'legacyWidget') {
            return this.widgetsFacade.removeWidget(historyChange.payload.entity, historyChange.payload.index, true);
        }

        if (historyChange.payload.entityType === 'tab') {
            return this.tabsFacade.remove(historyChange.payload.entity, historyChange.payload.index, true);
        }
    }

    private handleMoveRedo(historyChange: MoveHistoryChange): void {
        if (historyChange.payload.entityType === 'block') {
            if (historyChange.payload.positionTo < historyChange.payload.positionFrom) {
                return this.blocksFacade.moveDown(historyChange.payload.positionTo, true);
            }
            return this.blocksFacade.moveUp(historyChange.payload.positionTo, true);
        }

        if (historyChange.payload.entityType === 'column') {
            return this.columnsFacade.moveColumnToSection(
                historyChange.payload.entityId,
                historyChange.payload.prevParentId,
                historyChange.payload.nextParentId,
                historyChange.payload.positionFrom,
                historyChange.payload.positionTo,
                true
            );
        }

        if (historyChange.payload.entityType === 'legacyGridWidget') {
            if (historyChange.payload.type === 'fromGridToColumn') {
                return this.gridsFacade.moveWidgetFromGridToColumn(
                    historyChange.payload.entityId,
                    historyChange.payload.fromCell,
                    historyChange.payload.prevParentId,
                    historyChange.payload.nextParentId,
                    historyChange.payload.positionFrom,
                    historyChange.payload.positionTo,
                    true
                );
            }

            if (historyChange.payload.type === 'fromColumnToGrid') {
                return this.gridsFacade.moveWidgetFromColumnToGrid(
                    historyChange.payload.entityId,
                    historyChange.payload.toCell,
                    historyChange.payload.prevParentId,
                    historyChange.payload.nextParentId,
                    historyChange.payload.positionFrom,
                    historyChange.payload.positionTo,
                    true
                );
            }

            if (historyChange.payload.type === 'fromGridToGrid') {
                return this.gridsFacade.moveWidgetFromGridToGrid(
                    historyChange.payload.entityId,
                    historyChange.payload.fromCell,
                    historyChange.payload.toCell,
                    historyChange.payload.prevParentId,
                    historyChange.payload.nextParentId,
                    historyChange.payload.positionFrom,
                    historyChange.payload.positionTo,
                    true
                );
            }
        }

        if (historyChange.payload.entityType === 'legacyWidget') {
            return this.widgetsFacade.moveWidgetToColumn(
                historyChange.payload.entityId,
                historyChange.payload.prevParentId,
                historyChange.payload.nextParentId,
                historyChange.payload.positionFrom,
                historyChange.payload.positionTo,
                true
            );
        }

        console.warn('Unknown entity type', historyChange.payload.entityType);
    }

    private handleUpdateRedo(historyChange: UpdateHistoryChange | UpdateThemeHistoryChange): void {
        if (historyChange.payload.entityType === 'block') {
            return this.blocksFacade.updateBlockProperties(
                historyChange.payload.after.id as string,
                historyChange.payload.after.changes
            );
        }

        if (historyChange.payload.entityType === 'columns') {
            return this.columnsFacade.updateColumnsProperties(historyChange.payload.after);
        }

        if (historyChange.payload.entityType === 'grids') {
            return this.gridsFacade.updateManyProperties(historyChange.payload.after);
        }

        if (historyChange.payload.entityType === 'legacyWidget') {
            return this.widgetsFacade.updateLegacyWidgetProperties(
                historyChange.payload.after.id as string,
                historyChange.payload.after.changes
            );
        }

        if (historyChange.payload.entityType === 'tab') {
            return this.tabsFacade.updateProperties(
                historyChange.payload.after.id as string,
                historyChange.payload.after.changes
            );
        }

        if (historyChange.payload.entityType === 'store') {
            this.shopCartIconFacade.saveCartIcon(historyChange.payload.after.changes as any);
        }

        if (historyChange.payload.entityType === 'grid') {
            return this.gridsFacade.updateProperties(
                historyChange.payload.after.id as string,
                historyChange.payload.after.changes
            );
        }

        if (historyChange.payload.entityType === 'theme') {
            return this.themeState.restore(historyChange.payload.after);
        }

        console.warn('Unknown entity type', historyChange.payload.entityType);
    }
}
